import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { getWarehouseSummaryCostGraph } from "@/helpers/apis";
import CostsGraph from "./CostsGraph";
import { Typography, NativeSelect } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { CostGraphEntry, GetWarehouseSummaryCostGraphParams } from "../types";
import { RelativeComponentLoader } from "@/components/loader";
import { useWarehouseFilters } from "../hooks/useWarehouseFilters";

const AggregationOptions = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

const WarehouseSummaryCostGraph = () => {
  const { filterParams, isInitialized } = useWarehouseFilters();
  const [aggregation, setAggregation] =
    useState<GetWarehouseSummaryCostGraphParams["aggregation"]>("day");

  const { data: graphData, isLoading } = useQuery({
    queryKey: ["warehouse-summary-cost-graph", aggregation, filterParams],
    queryFn: () =>
      getWarehouseSummaryCostGraph({
        aggregation,
        ...filterParams,
      }),
    retry: false,
    enabled: isInitialized,
  });

  const formatDate = (date: string) => {
    return dayjs(date).format("DD MMM YY");
  };

  const roundToTwoDecimalPlaces = (number: number) => {
    return Math.round(number * 100) / 100;
  };

  // Transform API response to match CostsGraph expected format
  const stackedData = useMemo((): CostGraphEntry[] => {
    if (!graphData?.warehouse_cost || graphData.warehouse_cost.length === 0) return [];

    return graphData.warehouse_cost.map(entry => ({
      time: formatDate(entry.date),
      date: formatDate(entry.date),
      cost: roundToTwoDecimalPlaces(entry.cost),
      "cost over threshold": 0, // Not applicable for this graph
      threshold: 0, // Not applicable for this graph
      "total cost": roundToTwoDecimalPlaces(entry.cost),
      "compute cost": roundToTwoDecimalPlaces(entry.compute_cost),
      "cloud services cost": roundToTwoDecimalPlaces(entry.cloud_services_cost),
      "query acceleration cost": roundToTwoDecimalPlaces(entry.query_acceleration_cost),
      "cost under threshold": roundToTwoDecimalPlaces(entry.cost),
    }));
  }, [graphData]);

  if (!stackedData.length) {
    return null;
  }

  return (
    <div className="al-border-b al-border-b-gray-200 al-pt-4 al-pb-12 al-mb-4">
      <Stack direction="row" justify="between" align="center" className="al-mb-4">
        <Typography variant="section-heading">Total Warehouse Cost</Typography>
        <Stack align="center" aria-label="Aggregate dropdown">
          <Typography variant="body" size="xs">Aggregate by: </Typography>
          <NativeSelect
            size="sm"
            value={aggregation}
            onChange={(value: string) =>
              setAggregation(
                value as NonNullable<GetWarehouseSummaryCostGraphParams["aggregation"]>
              )
            }
            options={AggregationOptions}
            className="al-w-[130px]"
          />
        </Stack>
      </Stack>
      {isLoading ? (
        <RelativeComponentLoader label="Loading..." componentHeight={20} />
      ) : (
        <CostsGraph stackedData={stackedData} aggregation={aggregation} />
      )}
    </div>
  );
};

export default WarehouseSummaryCostGraph;
